/** @jsx jsx */
import {
  Button,
  Grid,
  Modal,
  Spacer,
  Spinner,
  Table,
  TextGroupMedium,
  useAsyncEffect,
} from "@cd2/components"
import { Fields } from "@cd2/forms"
import { H3, P, TextWrap } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import React, { useState } from "react"

import { useTable } from "@cd2/components/build/es6/table/useTable"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { ConfirmCancelDialogue } from "../../Components/ConfirmCancelDialogue"
import { FieldsRadioButtonSelect } from "../../Components/FieldsRadioCheckbox"
import { useEnsureGlobalState, useGlobalState } from "../../Components/GlobalStateProvider"
import { KeyValueList } from "../../Components/KeyValueList"
import { useProductGroupProductPrice } from "../../hooks/productGroupProductPrice"
import { useConfirmation } from "../../hooks/useConfirmation"
import { useManagedConfirmation } from "../../hooks/useManagedConfirmation"
import {
  ConfirmAlternateProductModal,
  useProductAlternative,
} from "../../hooks/useProductAlternative"
import { calculateCostPriceFromCurrency } from "../../stories/quoteOrders/ItemRowSageProduct"
import {
  calculateDiscountPrice,
  IItem,
  IQuoteOrderRecord,
} from "../../stories/quoteOrders/quoteOrderUtils"
import { IRecord } from "../../types/schema"
import { sum } from "../../utils/arrays"
import { currencyNameToSymbol } from "../../utils/currency"
import {
  getFilteredProductLocations,
  getProductLocationPrioritized,
} from "../../utils/getProductLocations"
import { getProductSuggestedPrice } from "../../utils/getProductSuggestedPrice"
import { productDpdFilter } from "../../utils/productDpdFilter"
import { useSettings } from "../../utils/useSettings"
import { formatPrice } from "../../utils/utils"
import { assert } from "@cd2/tools"

type IBuildSetItem = IRecord<"build_set_items", "quantity" | "weight" | "optional"> & {
  product: IRecord<"products", "stock_code" | "body">
  loadedData?: IBuildSetRow | null
}

type IBuildSet = IRecord<"build_sets", "customer" | "created_at" | "price_pennies"> & {
  product: IRecord<"products", "stock_code" | "body">
  created_by: IRecord<"skeem_users", "name">
  build_set_items: IBuildSetItem[]
}

type ISelectionType = "include" | "optional" | null
type IBuildSetResponse = {
  price_pennies: number | null
  product: IBuildSetItem
  items: IBuildSetItem[]
  itemIds: Record<string, ISelectionType>
}

export interface IBuildSetRow {
  item: Partial<IItem>
  callback: (itemId: string) => void
  optional: boolean
}

export function useBuildSetBuilder(
  quote: IQuoteOrderRecord,
  actions: {
    onAddPrimary: (item: IBuildSetRow, uid: string) => IItem
    onAddSubItem: (item: IBuildSetRow, uid: string) => void
    onComplete: (items: IItem, data: IBuildSetResponse, uid: string) => void
  },
) {
  const {
    defaultStockLocation,
    barScanOrders,
    warehouses,
    showDiscountOption,
    dpdProductPrefixes,
    stockWarehouses,
    useExplicitAllocations,
    _loaded,
    useQuoteOrderStandardPricing,
    directDeliveryWarehouse,
    ...settings
  } = useSettings()
  const { currentUser } = useEnsureGlobalState()

  const skeem = window.skeem

  const checkProductForAlternative = useProductAlternative()
  const getGroupCostPrice = useProductGroupProductPrice()

  function setWindowCustomerPrices(key: string, data: any) {
    ;(window as any).customerPrices ||= {}
    ;(window as any).customerPrices[key] = data
  }

  const [confirmAlternativeProductModal, confirmAlternativeProduct] = useConfirmation<
    any,
    { id: string }
  >(({ onResolve, data }) => <ConfirmAlternateProductModal data={data} onResolve={onResolve} />)

  async function loadProductDataForSageItemRow(
    pid: string,
  ): Promise<{ item: Partial<IItem>; callback: (itemId: string) => void }> {
    let productId = pid
    const altProduct = await checkProductForAlternative(productId)
    if (altProduct.alternative) {
      const selected = await confirmAlternativeProduct(altProduct)
      productId = selected.id
    }

    if (!productId) {
      await skeem.mutate({
        alerts: {
          create: {
            type: "useSetBuilder",
            body: `No product ID selected`,
            details: { pid, altProduct: altProduct },
            notifications: ["support@cd2solutions.co.uk", "henry@cd2solutions.co.uk"],
          },
        },
      })
    }
    assert(productId, "useBuildSetBuilder: No product ID selected")

    const dpdProductFilter = productDpdFilter(dpdProductPrefixes ?? "")

    type IProduct = IRecord<
      "products",
      | "stock_code"
      | "body"
      | "category"
      | "cost_price_pennies"
      | "line_item_description"
      | "sale_price_pennies"
      | "memo"
      | "price_updated_at"
      | "sage_id"
      | "is_consignment"
      | "original_cost_price_pennies"
      | "minimum_selling_price_pennies"
      | "minimum_margin"
      | "preferred_supplier_list_price_pennies"
      | "active"
    > & {
      branch: IRecord<"branches", "name" | "consignmentWarehouse" | "currency_name">
      product_locations: IRecord<
        "product_locations",
        "name" | "free_stock" | "warehouse" | "specific_location"
      >[]
      suppliers: IRecord<"suppliers", "currency_name">[]
      preferred_supplier: IRecord<"suppliers", "currency_name">
      product_group: IRecord<
        "product_groups",
        "products_price_method" | "memo" | "description" | "code" | "minimum_product_margin"
      >
    }
    const productFetch = skeem.fetchOne<IProduct>({
      products: {
        attributes: [
          "line_item_description",
          "body",
          "stock_code",
          "cost_price_pennies",
          "category",
          "sale_price_pennies",
          "memo",
          "price_updated_at",
          "sage_id",
          "is_consignment",
          "original_cost_price_pennies",
          "minimum_margin",
          "minimum_selling_price_pennies",
          "preferred_supplier_list_price_pennies",
          "active",
          { name: "branch", attributes: ["name", "consignmentWarehouse", "currency_name"] },
          { name: "suppliers", attributes: ["currency_name"] },
          { name: "preferred_supplier", attributes: ["currency_name"] },
          {
            name: "product_group",
            attributes: [
              "products_price_method",
              "memo",
              "description",
              "code",
              "minimum_product_margin",
            ],
          },
          {
            name: "product_locations",
            attributes: ["name", "free_stock", "warehouse", "specific_location"],
          },
        ],
        filter: {
          and: [
            { matches: { id: productId } },
            barScanOrders && dpdProductFilter.and.length > 0 && dpdProductFilter,
          ].filter(Boolean),
        },
      },
    })

    const pricing = await getProductSuggestedPrice(skeem, quote, productId)
    const product = await productFetch

    const locations = product.product_locations

    const productLocation = getProductLocationPrioritized(
      getFilteredProductLocations(locations),
      {
        defaultStockLocation: currentUser?.defaultWarehouse?.name ?? defaultStockLocation,
        branch: currentUser.branch,
        warehouses,
        defaultIntercoWarehouse: settings.defaultIntercoWarehouse,
        directDelivery: !!quote.delivery_method?.match(/direct/i),
        directDeliveryWarehouse: directDeliveryWarehouse,
      },
      { isConsignment: product.is_consignment, isInterco: quote.customer?.internalCustomer },
    )

    const useBody = !product.line_item_description

    const groupPrice = await getGroupCostPrice(product.id)
    const costPricePennies = calculateCostPriceFromCurrency({
      currency_name: quote.currency_name || currentUser?.branch.currency_name,
      currentUser,
      groupPrice,
      product,
    })

    const isHikKeyAccount = !!(
      pricing?.applyOpportunityCertificateBreakdown &&
      quote.hasOpportunityCertificate &&
      !useQuoteOrderStandardPricing
    )

    const costPricePenniesUpdated = isHikKeyAccount
      ? calculateDiscountPrice(costPricePennies, 0)
      : costPricePennies

    const itemData: Partial<IItem> = {
      product,
      product_location: productLocation,
      stock_code: product.stock_code,
      stock_location: productLocation?.name ?? "",
      body: useBody
        ? groupPrice?.body ?? product.body
        : groupPrice?.line_item_description ?? product.line_item_description,
      category: product.category,
      cost_price_pennies: costPricePenniesUpdated,
    }

    return {
      item: itemData,
      callback: (itemId: string) => setWindowCustomerPrices(itemId, pricing),
    }
  }

  async function prepareProductItem(
    item: IBuildSetItem,
    optional: boolean,
  ): Promise<null | IBuildSetRow> {
    if (!item.product?.id) {
      return null
    }
    const itemData = await loadProductDataForSageItemRow(item.product.id)

    return {
      callback: itemData.callback,
      item: {
        ...itemData.item,
        quantity: item.quantity,
        allocated_quantity: item.quantity,
      },
      optional: optional,
    }
  }

  const [selectBuildSetModal, handleSelectBuildSet] = useConfirmation<IBuildSet[], string | null>(
    ({ data, onResolve }) => {
      const [selectedBuild, setSelecetdBuild] = useState<(typeof data)[number] | null>(null)
      return (
        <ConfirmCancelDialogue
          heading="Select a build"
          onConfirm={() => onResolve(selectedBuild?.id ?? null)}
          onCancel={() => onResolve(null)}
          width={900}
          noOverflow
          isOpen
        >
          <Fields.Select2
            options={data}
            getOptionLabel={opt => opt.product.stock_code}
            filterOption={(opt, input) =>
              opt.data.product?.stock_code.includes(input) || opt.data.product?.body.includes(input)
            }
            getOptionValue={opt => opt.id}
            onChange={value => setSelecetdBuild(value)}
            formatOptionLabel={(opt, ctx) => {
              const inMenuSelected =
                ctx.context == "menu" && (ctx.selectValue as any)?.[0]?.id == opt.id
              return (
                <TextWrap blockSpacing={0} color={inMenuSelected ? "white" : undefined}>
                  <P>
                    {opt.product.stock_code}
                    {opt.customer ? ` (Customer Build)` : ""}
                  </P>
                  <P size="small">{opt.product.body}</P>
                  {ctx.context == "menu" && (
                    <KeyValueList
                      gap={4}
                      data={[
                        {
                          label: "Price Override",
                          value: opt.price_pennies
                            ? { price: opt.price_pennies, currency: "£", pennies: true }
                            : null,
                          returnNullOnEmpty: true,
                        },
                        {
                          label: "# Items",
                          value: (
                            <div>
                              <P size="small">{opt.build_set_items.length} items</P>
                            </div>
                          ),
                        },
                      ]}
                    />
                  )}
                </TextWrap>
              )
            }}
            value={selectedBuild}
          />
          <Spacer />
        </ConfirmCancelDialogue>
      )
    },
  )
  const [handleShowLoadingModal, loadingModal] = useManagedConfirmation<void, void>()

  const [manageSetModal, handleManageSet] = useConfirmation<
    {
      build_sets: IBuildSet
    },
    IBuildSetResponse | null
  >(({ data: { build_sets: data }, onResolve }) => {
    // optional items that should be included
    const [itemData, setItemData] = useState<IBuildSetItem[]>(data.build_set_items)
    const [itemIds, setItemIds] = useState<Record<string, ISelectionType>>(
      data.build_set_items.reduce(
        (acc, { id, optional }) => ({ ...acc, [id]: optional ? "optional" : "include" }),
        {} as Record<string, ISelectionType>,
      ),
    )
    function handleItemQuantityChange(itemId: string, quantity: number | null) {
      setItemData(prev => prev.map(item => (item.id == itemId ? { ...item, quantity } : item)))
    }

    function handleCancel() {
      onResolve(null)
    }

    function handleProceed() {
      const finalCopy: IBuildSetResponse = {
        price_pennies: data.price_pennies,
        product: {
          id: data.product.id,
          weight: 0,
          optional: false,
          product: data.product,
          quantity: 1,
        },
        items: itemData,
        itemIds: itemIds,
      }
      console.log("BUILD SET RESPONSE", finalCopy)
      onResolve(finalCopy)
    }

    function toggleId(
      setter: React.Dispatch<React.SetStateAction<Record<string, ISelectionType>>>,
      id: string,
      type: ISelectionType,
    ) {
      setter(prev => {
        if (prev[id] === type) {
          console.log("Clear type", id, type)
          return { ...prev, [id]: null }
        }
        console.log("set type", id, type)
        return { ...prev, [id]: type }
      })
    }

    function flattenToType(ids: string[], type: ISelectionType) {
      return ids.reduce((acc, id) => ({ ...acc, [id]: type }), {})
    }

    const includedItemData = itemData.filter(x => !!itemIds[x.id])

    return (
      <Modal
        width={900}
        render={() => (
          <div>
            <Grid gap={16}>
              <TextGroupMedium heading={data.product.stock_code} />

              <div
                css={css`
                  .col1 {
                    min-width: 105px;
                    width: 0%;
                  }

                  .col2 {
                    min-width: 178px;
                    width: 0%;
                  }

                  .col-wide {
                    width: 100%;
                  }
                `}
              >
                <React.Fragment>
                  <ButtonsWrapper>
                    <Button
                      onClick={() =>
                        setItemIds(
                          flattenToType(
                            itemData.map(item => item.id),
                            "include",
                          ),
                        )
                      }
                      extendTheme={{ padding: { sides: 10, topAndBottom: 8 } }}
                    >
                      Add all items
                    </Button>
                    <Button
                      onClick={() =>
                        setItemIds(
                          flattenToType(
                            itemData.map(item => item.id),
                            "optional",
                          ),
                        )
                      }
                      extendTheme={{ padding: { sides: 10, topAndBottom: 8 } }}
                    >
                      Add all items as optional
                    </Button>
                    <Button
                      onClick={() =>
                        setItemIds(
                          flattenToType(
                            itemData.map(item => item.id),
                            null,
                          ),
                        )
                      }
                      extendTheme={{ padding: { sides: 10, topAndBottom: 8 } }}
                    >
                      Remove all items
                    </Button>
                  </ButtonsWrapper>
                  <Spacer />
                  <Table
                    data={[
                      ...itemData,
                      {
                        type: "total",
                        cost_price_pennies: sum(includedItemData, item => {
                          const price = item.loadedData?.item.cost_price_pennies ?? 0
                          return price
                        }),
                        total_price_pennies: sum(includedItemData, item => {
                          const price = item.loadedData?.item.cost_price_pennies ?? 0
                          const qty = item.quantity ?? 0
                          return price * qty
                        }),
                      },
                    ]}
                  >
                    <CustomItemTableRow
                      itemIds={itemIds}
                      toggleId={(id, type) => toggleId(setItemIds, id, type)}
                      onChangeQuantity={handleItemQuantityChange}
                    />
                  </Table>
                </React.Fragment>
                <Spacer />
              </div>
              <ConfirmCancelButtons
                buttonOpts={{ confirm: { children: "Finalize" } }}
                onConfirm={handleProceed}
                onCancel={handleCancel}
              />
            </Grid>
          </div>
        )}
        isOpen
      />
    )
  })

  async function loadBuildSet(id: string) {
    const set = await skeem.fetch2<IBuildSet>({
      first: {
        name: "build_sets",
        attributes: [
          "customer",
          "created_at",
          "price_pennies",
          { name: "product", attributes: ["stock_code", "body"] },
          { name: "created_by", attributes: ["name"] },
          {
            name: "build_set_items",
            attributes: [
              "quantity",
              "weight",
              "optional",
              { name: "product", attributes: ["stock_code", "body"] },
            ],
            sort: { by: "weight", direction: "asc" },
          },
        ],
        matches: { id },
      },
    })
    return set
  }

  async function handleCreateBuildSet(branchId: string, customerId?: string) {
    const sets = await skeem.fetch2<IBuildSet[]>({
      model: {
        name: "build_sets",
        attributes: [
          "customer",
          "created_at",
          "price_pennies",
          { name: "product", attributes: ["stock_code", "body"] },
          { name: "created_by", attributes: ["name"] },
          {
            name: "build_set_items",
            attributes: [
              "quantity",
              "weight",
              "optional",
              { name: "product", attributes: ["stock_code", "body"] },
            ],
            sort: { by: "weight", direction: "asc" },
          },
        ],
        filter: {
          and: [
            { matches: { branch: branchId } },
            {
              or: [
                { matches: { customer: null } },
                customerId && { matches: { customer: customerId } },
              ].filter(Boolean),
            },
          ],
        },
        sort: [
          { by: { path: ["customer", "id"] }, direction: "DESC" },
          { by: { path: ["product", "stock_code"] }, direction: "ASC" },
        ],
      },
    })
    console.log("LOADED BUILD SETS", sets)
    const selectedBuildId = await handleSelectBuildSet(sets)
    if (!selectedBuildId) {
      return null
    }

    // run the first build set / parent build set manually
    const result = await loadBuildSet(selectedBuildId)
    handleShowLoadingModal()
    for (const item of result.build_set_items) {
      item.loadedData = await prepareProductItem(item, false)
    }
    loadingModal.onResolve()
    const initialResponse = await handleManageSet({ build_sets: result })
    if (!initialResponse) {
      return null
    }

    return handleDataPrep(initialResponse)
  }
  const dataInsertRunning = React.useRef(false)
  const [prepareDataModal, handleDataPrep] = useConfirmation<IBuildSetResponse, boolean>(
    ({ data, onResolve }) => {
      async function handlePrepareData() {
        if (dataInsertRunning.current) {
          return
        }
        dataInsertRunning.current = true
        try {
          const preparedItem = await prepareProductItem(data.product, false)
          if (!preparedItem) {
            throw new Error("Error adding primary product for this build")
          }
          if (preparedItem) {
            preparedItem.item.price_pennies = data.price_pennies
          }
          const uid = Math.random().toString(36).substring(7)
          const row = actions.onAddPrimary(preparedItem, uid)

          const ids = data?.itemIds ?? {}

          for (const item of data?.items ?? []) {
            if (ids[item.id] === "include" || ids[item.id] === "optional") {
              const preparedItem = data.items.find(i => i.id == item.id)?.loadedData
              if (preparedItem) {
                preparedItem.optional = ids[item.id] === "optional"
                preparedItem.item.quantity = item.quantity
                actions.onAddSubItem(preparedItem, uid)
              }
            }
          }
          actions.onComplete(row, data, uid)
          onResolve(true)
        } catch (e) {
          console.error(e)
          onResolve(false)
        } finally {
          dataInsertRunning.current = false
        }
      }

      useAsyncEffect(handlePrepareData, [])
      return (
        <Modal
          render={() => (
            <div>
              <Spacer />
              <H3 align="center">Processing changes</H3>
              <Spacer />
              <P align="center">Please wait for the items to be loaded.</P>
              <P align="center">This modal will close automatically once done.</P>
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                `}
              >
                <Spinner size={64} color="black" />
              </div>
              <Spacer />
            </div>
          )}
          isOpen
        />
      )
    },
  )

  return {
    handleCreateBuildSet,
    loadProductDataForSageItemRow,
    modals: (
      <React.Fragment key="build-set-loader-modals">
        <Modal
          render={() => (
            <div>
              <Spacer />
              <H3 align="center">Preparing data</H3>
              <Spacer />
              <P align="center">Please wait for the items to be loaded.</P>
              <P align="center">This modal will close automatically once done.</P>
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                `}
              >
                <Spinner size={64} color="black" />
              </div>
              <Spacer />
            </div>
          )}
          isOpen={loadingModal.showDialog}
        />
        {selectBuildSetModal}
        {manageSetModal}
        {confirmAlternativeProductModal}
        {prepareDataModal}
      </React.Fragment>
    ),
  }
}

function CustomItemTableRow({
  toggleId,
  itemIds,
  onChangeQuantity,
}: {
  toggleId: (id: string, type: ISelectionType) => void
  itemIds: Record<string, ISelectionType>
  onChangeQuantity: (itemId: string, quantity: number | null) => void
}) {
  const { currentUser } = useGlobalState()
  const table = useTable()
  if (table.isHeading) {
    return (
      <React.Fragment>
        <Table.Column headingClassName="col1">{() => null}</Table.Column>
        <Table.Column headingClassName="col2">{() => null}</Table.Column>
        <Table.Column heading="Item">{() => null}</Table.Column>
        <Table.Column heading="Quantity">{() => null}</Table.Column>
        <Table.Column heading="Cost Price">{() => null}</Table.Column>
        <Table.Column heading="Total">{() => null}</Table.Column>
      </React.Fragment>
    )
  }
  // const row = table.row as IBuildSetItem
  return (
    <React.Fragment>
      <Table.Column headingClassName="col1">
        {row =>
          row.type == "total" ? null : (
            <FieldsRadioButtonSelect
              name={`include-${row.id}`}
              onChange={() => toggleId(row.id, "include")}
              value={itemIds[row.id]}
              options={[{ label: "Include", value: "include" }]}
              clearOnReselect
            />
          )
        }
      </Table.Column>
      <Table.Column headingClassName="col2">
        {row =>
          row.type == "total" ? null : (
            <FieldsRadioButtonSelect
              name={`include-${row.id}`}
              onChange={() => toggleId(row.id, "optional")}
              value={itemIds[row.id]}
              options={[{ label: "Include as optional", value: "optional" }]}
              clearOnReselect
            />
          )
        }
      </Table.Column>
      <Table.Column heading="Item">
        {row =>
          row.type == "total" ? null : (
            <TextWrap blockSpacing={0}>
              <P size="small">Product</P>
              <P>{row.product?.stock_code}</P>
              <P>{row.product?.body}</P>
            </TextWrap>
          )
        }
      </Table.Column>
      <Table.Column heading="Quantity">
        {row =>
          row.type == "total" ? null : (
            <Fields.Number2 onChange={val => onChangeQuantity(row.id, val)} value={row.quantity} />
          )
        }
      </Table.Column>
      <Table.Column heading="Cost Price">
        {row => {
          if (row.type === "total") {
            return formatPrice(row.cost_price_pennies, {
              isPennies: true,
              currency: currencyNameToSymbol(currentUser?.branch.currency_name),
            })
          }

          if (!itemIds[row.id]) {
            return null
          }
          const price = row?.loadedData?.item?.cost_price_pennies
          if (price) {
            return formatPrice(price, {
              isPennies: true,
              currency: currencyNameToSymbol(currentUser?.branch.currency_name),
            })
          }
          return null
        }}
      </Table.Column>
      <Table.Column heading="Total">
        {row => {
          if (row.type === "total") {
            return formatPrice(row.total_price_pennies, {
              isPennies: true,
              currency: currencyNameToSymbol(currentUser?.branch.currency_name),
            })
          }
          if (!itemIds[row.id]) {
            return null
          }
          const price = row?.loadedData?.item?.cost_price_pennies
          if (price) {
            return formatPrice(price * (row.quantity ?? 0), {
              isPennies: true,
              currency: currencyNameToSymbol(currentUser?.branch.currency_name),
            })
          }
          return null
        }}
      </Table.Column>
    </React.Fragment>
  )
}
